<script setup lang="ts">
import { useContactList } from '../hooks/useContactList';
import { IRtl } from '../utils/interfaces';

interface ContactFormContactListProps {
  rtlItem: IRtl
}

const { currentMarket,
        getContactListOptions,
        getContactListToShow,
        showLocalSection,
        showOtherSection,
        showOtherWebsite
       } = useContactList();

const reduceOption = (option: { label: string; value: string }) => option.value;
defineProps<ContactFormContactListProps>();
</script>

<template>
  <div class="contact-list-container">
    <div 
      class="dropdown-intro" 
      data-pw-form="contact-list-dropdown-intro"
      >{{ getContactListToShow.dropdownIntroduction }}</div>
      <v-select 
        :options="getContactListOptions"
        label="label"
        :reduce="reduceOption"
        v-model="currentMarket"
        :aria-label="getContactListToShow.dropdownLabel"
        :dir="rtlItem.currentDirection"
        :class="{ 'text-rtl': rtlItem.useRtl }"
        data-pw-form="contact-list-options"
      >
        <template #no-options>
          <div>{{ getContactListToShow.dropdownError }}</div>
        </template>
      </v-select>
    </div>
  <div class="contact-list-details">

    <div v-if="showLocalSection" class="contact-list-group local-contact" data-pw-form="contact-list-local-contact-section">
      <p v-if="getContactListToShow.localInfo" class="contact-list-section" data-pw-form="contact-list-local-info">
        {{ getContactListToShow.localInfo }}
      </p>
      <p v-if="getContactListToShow.localName" class="contact-list-section" data-pw-form="contact-list-local-name">
        {{ getContactListToShow.localName }}
      </p>
      <div class="contact-list-section">
        <a v-if="getContactListToShow.localEmail"
          data-pw-form="contact-list-local-email"
          :href="`mailto:${getContactListToShow.localEmail}`">
          {{ getContactListToShow.localEmail }}
        </a>
      </div>
      <div class="contact-list-section">
        <a v-if="getContactListToShow.localPhone" 
          data-pw-form="contact-list-local-phone"
          :href="`tel:${getContactListToShow.localPhone}`">
          {{ getContactListToShow.localPhone }}
        </a>
      </div>
      <p v-if="getContactListToShow.localAddress" class="contact-list-section" data-pw-form="contact-list-local-address">
        {{ getContactListToShow.localAddress }}
      </p>
    </div>

    <div v-if="showOtherSection" class="contact-list-group other-contact" data-pw-form="contact-list-other-contact-section">
      <p v-if="getContactListToShow.otherInfo" class="contact-list-section" data-pw-form="contact-list-other-info">
        {{ getContactListToShow.otherInfo }}
      </p>
      <div v-if="showOtherWebsite" class="contact-list-section" data-pw-form="contact-list-other-website">
        <a 
          :href="getContactListToShow.otherWebsiteUrl"
          :aria-label="getContactListToShow.otherWebsiteName"
          target="_blank" 
          rel="noopener noreferrer"
        >
          {{ getContactListToShow.otherWebsiteName }}
        </a>
      </div>
      <p v-if="getContactListToShow.otherName" class="contact-list-section" data-pw-form="contact-list-other-name">
        {{ getContactListToShow.otherName }}
      </p>
      <div class="contact-list-section">
        <a :href="`mailto:${getContactListToShow.otherEmail}`" v-if="getContactListToShow.otherEmail" data-pw-form="contact-list-other-email">
          {{ getContactListToShow.otherEmail }}
        </a>
      </div>
      <div class="contact-list-section">
        <a :href="`tel:${getContactListToShow.otherPhone}`" v-if="getContactListToShow.otherPhone" data-pw-form="contact-list-other-phone">
          {{ getContactListToShow.otherPhone }}
        </a>
      </div>

      <p v-if="getContactListToShow.otherAddress" class="contact-list-section" data-pw-form="contact-list-other-address">
        {{ getContactListToShow.otherAddress }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "../../site/variables";
@use "../../site/mixins";


.contact-list-container {
  margin-top: 1rem;
}

.contact-list-details {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.contact-list-group {
  padding: 1.5rem;
  border-radius: 10px;
  background: #fafafa;
  border: 1px solid #e6e6e6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  .contact-list-section {
    position: relative;
    padding-left: 1.2rem;
    margin: 0.5rem 0;
    font-size: 1rem;
    line-height: 1.4;

    &::before {
      content: "•";
      position: absolute;
      left: 0;
      top: 0;
      font-size: 1.2rem;
      color: variables.$color-black;
    }

    a {
      color: variables.$color-black;
      text-decoration: underline;
    }
  }

  .contact-list-section:first-child {
    font-weight: 600;
    font-size: 1.1rem;
    color: variables.$color-black;
    padding: 0;

    &::before {
      display: none;
    }
  }
}

.dropdown-intro {
  @include mixins.dropdown-styles-basic;
}

:deep(.vs__dropdown-toggle) {
  @include mixins.dropdown-toggle;
}

.text-rtl :deep(.vs__search),
.text-rtl :deep(.vs__selected) {
  padding-right: 1.5rem;
}

</style>
